import React from "react";
import {
  Box,
  Typography,
  Stack,
  Checkbox,
  LinearProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { PasswordRequirements } from "../utils/passwordValidation";

interface PasswordStrengthIndicatorProps {
  requirements: PasswordRequirements[];
  showProgress?: boolean;
}

export const PasswordStrengthIndicator: React.FC<
  PasswordStrengthIndicatorProps
> = ({ requirements, showProgress = true }) => {
  const validCount = requirements.filter((req) => req.isValid).length;
  const totalCount = requirements.length;
  const strengthPercentage = (validCount / totalCount) * 100;

  const getStrengthColor = (percentage: number) => {
    if (percentage < 40) return "error";
    if (percentage < 80) return "warning";
    return "success";
  };

  const getStrengthLabel = (percentage: number) => {
    if (percentage < 40) return "Weak";
    if (percentage < 80) return "Medium";
    return "Strong";
  };

  return (
    <Box sx={{ mt: 2 }}>
      {showProgress && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Password strength
            </Typography>
            <Typography
              variant="body2"
              color={`${getStrengthColor(strengthPercentage)}.main`}
              sx={{ fontWeight: 500 }}
            >
              {getStrengthLabel(strengthPercentage)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={strengthPercentage}
            color={getStrengthColor(strengthPercentage) as any}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: "divider",
            }}
          />
        </Box>
      )}

      <Stack spacing={1}>
        {requirements.map((requirement) => (
          <Box
            key={requirement.key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {requirement.isValid ? (
              <CheckCircleOutlineIcon
                sx={{
                  fontSize: 16,
                  color: "success.main",
                }}
              />
            ) : (
              <RadioButtonUncheckedIcon
                sx={{
                  fontSize: 16,
                  color: "text.disabled",
                }}
              />
            )}
            <Typography
              variant="body2"
              sx={{
                color: requirement.isValid ? "success.main" : "text.secondary",
                fontSize: "0.875rem",
              }}
            >
              {requirement.label}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
